import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, catchError } from 'rxjs';

import { BaseApiService } from '../../core/services/base-api.service';
import { KohaBiblioDto, KohaItemDto } from '../../types/api.types';

/**
 * BiblioDetailService — HTTP layer for the Biblio Details page.
 *
 * Endpoints:
 *  1. GET /api/v1/biblios/{id}
 *       → Tries x-koha-embed: biblioitem first.
 *         If the server rejects embedding ("Embedding objects is not allowed"),
 *         falls back to a plain request without the header.
 *  2. GET /api/v1/items?q={biblio_id:...}
 *       → NO x-koha-embed (not supported on /items).
 */
@Injectable({ providedIn: 'root' })
export class BiblioDetailService extends BaseApiService {
  protected override basePath = '/api/v1';

  /**
   * Fetch a single biblio record.
   * Sends x-koha-embed: biblioitem to inline biblioitem fields.
   * Falls back to plain request if this Koha instance disallows embedding.
   */
  fetchBiblio(id: number): Observable<KohaBiblioDto> {
    return this.http.get<KohaBiblioDto>(`${this.basePath}/biblios/${id}`);
  }

  /**
   * Fetch all physical copies (items) for a given biblio.
   * Uses GET /api/v1/items with Koha JSON query syntax.
   *
   * IMPORTANT: x-koha-embed is intentionally omitted.
   * The /items endpoint rejects it with:
   * "Embedding objects is not allowed on this endpoint."
   */
  fetchItems(biblioId: number): Observable<KohaItemDto[]> {
    const params = new HttpParams()
      .set('q', JSON.stringify({ biblio_id: biblioId }))
      .set('_per_page', '50');

    return this.get<KohaItemDto[]>('/items', params);
  }

  /**
   * Fetch the raw MARC-in-JSON record for a biblio.
   */
  fetchMarc(biblioId: number): Observable<any> {
    return this.http.get<any>(`${this.basePath}/biblios/${biblioId}`, {
      headers: {
        Accept: 'application/marc-in-json'
      }
    });
  }
}
